import { createContext } from 'react';
import { useCallback } from 'react';
import { useContext } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReadOrganizationMembershipCache } from '@socialgoodsoftware/components';
import { WriteOrganizationMembershipCache } from '@socialgoodsoftware/components';
import { useOrgSync } from '@socialgoodsoftware/components';
import { useSessionStatus } from '@socialgoodsoftware/components';
import { organizationApi } from '../organization-api';
import { userApi } from '../user-api';
import { useRunAs } from './RunAsContext';
import { featureQueryKey } from '../lib/api-client';
import { featureRoutes } from '../config/feature-routes';
import { buildLoginRedirectForCurrentLocation } from '../lib/login-redirect';
import { setOrganizationDeactivatedHandler } from '../lib/organization-deactivated-signal';
import { ClearOrganizationScopedState } from '../lib/organization-scoped-state';
import { completeAuthenticatedLanding } from '../lib/complete-authenticated-landing';
import { ParseStoredSelection } from '../lib/selected-organization';
import { selectedOrgStorageKey } from '../lib/selected-organization';

import type { ReactNode } from 'react';
import type { OrganizationSelection } from '@socialgoodsoftware/components';
import type { QueryResponse } from '@socialgoodsoftware/components';
import type { OrganizationResponse } from '@contracts/account/organization';
import type { OrganizationSummary } from '@contracts/account/organization';
import type { UserRecord } from '@contracts/account/user';

export type { OrganizationSelection };

type OrganizationContextValue = {
  user: UserRecord | null;
  organization: OrganizationResponse | null;
  /** Active org for shell display (name from getOrganization / Postgres). Precedence is switcher-only. */
  organizationSelection: OrganizationSelection | null;
  /** Non-null while switchOrganization is in flight — target from the switcher (name + precedence). */
  switchingToOrganization: OrganizationSelection | null;
  organizations: OrganizationSummary[];
  organizationsLoading: boolean;
  /** Cache-first membership list for the org switcher (network only when menu needs it). */
  loadOrganizations: () => void;
  /** The switcher's own entry: guarded against re-picking the active organization, reloads on failure. */
  selectOrganization: (selection: OrganizationSelection) => void;
  /** Switches and lands, throwing the raw failure so the caller can render it. */
  switchOrganization: (selection: OrganizationSelection, continueUrl: string | null) => Promise<void>;
  recoverFromDeactivation: () => Promise<void>;
  refresh: () => void;
  applyOrganizationRename: (newName: string) => void;
  isLoading: boolean;
};

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const defaultLandingPath = '/dashboard';
const loginPath = '/login';

function IsSameSelection(
  left: OrganizationSelection | null | undefined,
  right: OrganizationSelection | null | undefined
): boolean {
  let same = false;
  if (left && right && left.name === right.name && left.precedence === right.precedence) {
    same = true;
  }
  return same;
}

function FindActivePrecedence(list: OrganizationSummary[], name: string): number | null {
  let precedence: number | null = null;
  for (const entry of list) {
    if (entry.isActive && entry.name === name) {
      precedence = entry.precedence;
      break;
    }
  }
  return precedence;
}

function getAppBasePath(pathname: string | null): string {
  let base = defaultLandingPath;
  if (pathname) {
    const firstSegment = pathname.split('/').filter(Boolean)[0];
    if (firstSegment) {
      base = `/${firstSegment}`;
    }
  }
  return base;
}

function routeRequiresSession(pathname: string): boolean {
  let requires = true;
  const match = featureRoutes.find((route) => route.path === pathname);
  if (match) {
    requires = match.archetype !== 'auth';
  }
  return requires;
}

function MembershipCacheUserKey(user: UserRecord | null): string {
  let key = '';
  if (user) {
    if (!Empty(user.id)) {
      key = String(user.id);
    } else if (!Empty(user.email)) {
      key = String(user.email);
    } else if (!Empty(user.cognitoSub)) {
      key = String(user.cognitoSub);
    }
  }
  return key;
}

function Empty(value: unknown): boolean {
  let empty = true;
  if (value !== undefined && value !== null) {
    if (typeof value === 'string') {
      empty = value.trim().length === 0;
    } else {
      empty = false;
    }
  }
  return empty;
}

function MapSummariesToCache(list: OrganizationSummary[]): OrganizationSelection[] {
  return list.map((entry) => ({
    name: entry.name,
    precedence: entry.precedence,
  }));
}

function MapCacheToSummaries(list: OrganizationSelection[]): OrganizationSummary[] {
  return list.map((entry) => ({
    name: entry.name,
    precedence: entry.precedence,
    isActive: false,
  }));
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { clearRunAs } = useRunAs();
  const switchOrganizationMutation = organizationApi.switchOrganization.useMutation();

  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationSelection | null>(() => {
    let stored: OrganizationSelection | null = null;
    if (typeof window !== 'undefined') {
      stored = ParseStoredSelection(window.localStorage.getItem(selectedOrgStorageKey));
    }
    return stored;
  });
  const [switchingToOrganization, setSwitchingToOrganization] = useState<OrganizationSelection | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const listFetchInFlight = useRef(false);
  const deactivationInFlightRef = useRef(false);
  const requiresSession = routeRequiresSession(location.pathname);

  const userQuery = userApi.getUser.useQuery({
    queryKey: featureQueryKey('user'),
    enabled: requiresSession,
    retry: false,
  });
  let userAuthenticated = false;
  if (userQuery.isSuccess && userQuery.data && userQuery.data.status === 200) {
    userAuthenticated = true;
  }
  const organizationQuery = organizationApi.getOrganization.useQuery({
    queryKey: featureQueryKey('organization', 'active'),
    queryData: { query: {} },
    enabled: requiresSession,
    retry: false,
  });

  const user = useMemo<UserRecord | null>(() => {
    let value: UserRecord | null = null;
    if (userQuery.isSuccess && userQuery.data && userQuery.data.status === 200) {
      value = userQuery.data.body;
    }
    return value;
  }, [userQuery.isSuccess, userQuery.data]);

  const organization = useMemo<OrganizationResponse | null>(() => {
    let value: OrganizationResponse | null = null;
    if (organizationQuery.isSuccess && organizationQuery.data && organizationQuery.data.status === 200) {
      value = organizationQuery.data.body;
    }
    return value;
  }, [organizationQuery.isSuccess, organizationQuery.data]);

  const resolvedSelection = useMemo<OrganizationSelection | null>(() => {
    let resolved: OrganizationSelection | null = null;
    if (organization && typeof organization.name === 'string' && organization.name.length > 0) {
      let precedence = 0;
      const fromList = FindActivePrecedence(organizations, organization.name);
      if (fromList !== null) {
        precedence = fromList;
      } else if (selectedOrganization && selectedOrganization.name === organization.name) {
        precedence = selectedOrganization.precedence;
      }
      resolved = {
        name: organization.name,
        precedence: precedence,
      };
    } else if (selectedOrganization && selectedOrganization.name.length > 0) {
      resolved = {
        name: selectedOrganization.name,
        precedence: selectedOrganization.precedence,
      };
    }
    return resolved;
  }, [organization, organizations, selectedOrganization]);

  useEffect(() => {
    if (organization && resolvedSelection !== null) {
      window.localStorage.setItem(selectedOrgStorageKey, JSON.stringify(resolvedSelection));
    }
  }, [organization, resolvedSelection]);

  const applyOrganizationList = useCallback((list: OrganizationSummary[], cacheUserKey: string) => {
    setOrganizations(list);
    if (cacheUserKey.length > 0) {
      WriteOrganizationMembershipCache(cacheUserKey, MapSummariesToCache(list));
    }
  }, []);

  const fetchOrganizationList = useCallback(
    async (options: { showLoading: boolean }) => {
      const cacheUserKey = MembershipCacheUserKey(user);
      if (listFetchInFlight.current) {
        return;
      }
      listFetchInFlight.current = true;
      if (options.showLoading) {
        setOrganizationsLoading(true);
      }
      try {
        const response = await organizationApi.listForUser.query({});
        if (response.status === 200 && response.body && Array.isArray(response.body.organizations)) {
          applyOrganizationList(response.body.organizations, cacheUserKey);
        }
      } catch {
        /* list stays at cache / empty; switcher shows empty state */
      }
      listFetchInFlight.current = false;
      setOrganizationsLoading(false);
    },
    [user, applyOrganizationList]
  );

  const loadOrganizations = useCallback(() => {
    const cacheUserKey = MembershipCacheUserKey(user);
    let hadCache = false;
    if (cacheUserKey.length > 0) {
      const cached = ReadOrganizationMembershipCache(cacheUserKey);
      if (cached && cached.length > 0) {
        setOrganizations(MapCacheToSummaries(cached));
        hadCache = true;
      }
    }
    if (organizations.length > 0) {
      hadCache = true;
    }
    if (hadCache) {
      void fetchOrganizationList({ showLoading: false });
    } else {
      void fetchOrganizationList({ showLoading: true });
    }
  }, [user, organizations.length, fetchOrganizationList]);

  const refresh = useCallback(() => {
    userQuery.refetch().catch(() => {
      // surfacing handled by react-query state
    });
    organizationQuery.refetch().catch(() => {
      // surfacing handled by react-query state
    });
    void fetchOrganizationList({ showLoading: false });
  }, [userQuery, organizationQuery, fetchOrganizationList]);

  const applyOrganizationRename = useCallback(
    (newName: string) => {
      const trimmedName = newName.trim();
      if (trimmedName.length > 0) {
        setOrganizations((previous) =>
          previous.map((entry) => {
            let mapped = entry;
            if (entry.isActive) {
              mapped = { ...entry, name: trimmedName };
            }
            return mapped;
          })
        );
        queryClient.setQueryData(
          featureQueryKey('organization', 'active'),
          (previous: QueryResponse<OrganizationResponse> | undefined) => {
            let next = previous;
            if (previous && previous.status === 200) {
              next = { status: previous.status, body: { ...previous.body, name: trimmedName } };
            }
            return next;
          }
        );
        void queryClient.invalidateQueries({ queryKey: featureQueryKey('organization', 'active') });
        void fetchOrganizationList({ showLoading: false });
      }
    },
    [queryClient, fetchOrganizationList]
  );

  // Shell waits only on the session organization (and user is gated by RequireAuth).
  const isLoading = organizationQuery.isLoading;

  const adoptOrganization = useCallback(
    (selection: OrganizationSelection): void => {
      window.localStorage.setItem(selectedOrgStorageKey, JSON.stringify(selection));
      setSelectedOrganization(selection);
      queryClient.clear();
      clearRunAs();
      ClearOrganizationScopedState();
    },
    [queryClient, clearRunAs]
  );

  const { broadcastOrgSwitch, reactToOrganizationSelection } = useOrgSync({
    selectedOrganization: resolvedSelection,
    onSeed: (selection) => {
      setSelectedOrganization(selection);
    },
    onResync: (selection) => {
      adoptOrganization(selection);
      let continueUrl: string | null = null;
      if (requiresSession) {
        continueUrl = getAppBasePath(location.pathname);
      }
      void completeAuthenticatedLanding({
        queryClient: queryClient,
        navigate: navigate,
        continueUrl: continueUrl,
      });
    },
  });

  const switchOrganization = useCallback(
    async (selection: OrganizationSelection, continueUrl: string | null): Promise<void> => {
      setSwitchingToOrganization(selection);
      try {
        const response = await switchOrganizationMutation.mutateAsync({
          body: {
            name: selection.name,
            precedence: selection.precedence,
          },
        });
        if (response.status !== 200) {
          throw response;
        }
        adoptOrganization(selection);
        broadcastOrgSwitch(selection);
        await completeAuthenticatedLanding({
          queryClient: queryClient,
          navigate: navigate,
          continueUrl: continueUrl,
        });
        setSwitchingToOrganization(null);
      } catch (error) {
        setSwitchingToOrganization(null);
        throw error;
      }
    },
    [switchOrganizationMutation, adoptOrganization, broadcastOrgSwitch, queryClient, navigate]
  );

  const selectOrganization = useCallback(
    async (selection: OrganizationSelection): Promise<void> => {
      if (!IsSameSelection(selection, resolvedSelection) && switchingToOrganization === null) {
        try {
          await switchOrganization(selection, getAppBasePath(location.pathname));
        } catch {
          window.location.reload();
        }
      }
    },
    [resolvedSelection, switchingToOrganization, switchOrganization, location.pathname]
  );

  const recoverFromDeactivation = useCallback(async (): Promise<void> => {
    if (deactivationInFlightRef.current) {
      return;
    }
    deactivationInFlightRef.current = true;
    try {
      let candidates: OrganizationSummary[] = [];
      try {
        const listResponse = await organizationApi.listForUser.query({});
        if (listResponse.status === 200 && Array.isArray(listResponse.body.organizations)) {
          candidates = listResponse.body.organizations.filter(
            (entry) => !IsSameSelection({ name: entry.name, precedence: entry.precedence }, resolvedSelection)
          );
        }
      } catch {
        candidates = [];
      }
      let switched = false;
      for (let index = 0; index < candidates.length && !switched; index = index + 1) {
        const candidate = candidates[index];
        try {
          await switchOrganization(
            { name: candidate.name, precedence: candidate.precedence },
            getAppBasePath(location.pathname)
          );
          switched = true;
        } catch {
          switched = false;
        }
      }
      if (!switched) {
        navigate('/logout', { replace: true });
      }
    } finally {
      deactivationInFlightRef.current = false;
    }
  }, [resolvedSelection, switchOrganization, location.pathname, navigate]);

  useSessionStatus({
    redirectUrl: loginPath,
    resolveRedirectUrl: buildLoginRedirectForCurrentLocation,
    onOrgChange: reactToOrganizationSelection,
    onOrgDeactivated: () => {
      void recoverFromDeactivation();
    },
    enabled: requiresSession && userAuthenticated && !isLoading && switchingToOrganization === null,
  });

  useEffect(() => {
    setOrganizationDeactivatedHandler(() => {
      void recoverFromDeactivation();
    });
    return () => {
      setOrganizationDeactivatedHandler(null);
    };
  }, [recoverFromDeactivation]);

  const value: OrganizationContextValue = {
    user: user,
    organization: organization,
    organizationSelection: resolvedSelection,
    switchingToOrganization: switchingToOrganization,
    organizations: organizations,
    organizationsLoading: organizationsLoading,
    loadOrganizations: loadOrganizations,
    selectOrganization: selectOrganization,
    switchOrganization: switchOrganization,
    recoverFromDeactivation: recoverFromDeactivation,
    refresh: refresh,
    applyOrganizationRename: applyOrganizationRename,
    isLoading: isLoading,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
